import { z } from "zod";

import { COGNITO_INTERNAL_STAFF_GROUPS } from "@recobra/core";

import { BrazilBankingInfoSchema, brazilPhoneSchema } from "../client";

export const AdminUserDetailSchema = z.object({
  username: z.string(),
  status: z.string().optional(),
  enabled: z.boolean().optional(),
  createdAt: z.string().optional(),
  modifiedAt: z.string().optional(),
  attributes: z.record(z.string(), z.string()),
  groups: z.array(z.string()).optional(),
});

export type AdminUserSummary = z.infer<typeof AdminUserDetailSchema>;

export const ListUsersQuerySchema = z
  .object({
    q: z.string().max(200).optional(),
    limit: z.coerce.number().int().min(1).max(60).default(20),
    paginationToken: z.string().optional(),
    sortBy: z.enum(["displayName"]).default("displayName"),
    sortOrder: z.enum(["asc", "desc"]).default("asc"),
  })
  .describe("Paginated admin user list query parameters.");
export type ListUsersQuery = z.infer<typeof ListUsersQuerySchema>;

export const ListAdminUsersResponseSchema = z.object({
  users: z.array(AdminUserDetailSchema),
  paginationToken: z.string().optional(),
});
export type ListAdminUsersResponse = z.infer<typeof ListAdminUsersResponseSchema>;

export type AdminUserDetail = z.infer<typeof AdminUserDetailSchema>;

export const ListAdminGroupsResponseSchema = z.object({
  groups: z.array(z.string()),
});

export const CreateAdminUserSchema = z.object({
  email: z.email(),
  name: z.string().optional(),
  groupName: z.string().optional(),
});
export type CreateAdminUserInput = z.infer<typeof CreateAdminUserSchema>;

export const CreateAdminUserResponseSchema = z.object({
  username: z.string().optional(),
  status: z.string().optional(),
});

const UserContactInfoSchema = z.object({
  phone1: brazilPhoneSchema,
  phone2: brazilPhoneSchema,
  address: z.string().max(300).optional(),
});

/**
 * Canonical shape of a stored user profile record (`UsersTable`,
 * `USER_PROFILE` sort key) — the single source of truth for these fields'
 * validation rules. `UpsertUserProfileSchema` (write) and
 * `UserProfileResponseSchema` (read) both extend this rather than
 * redeclaring the same fields, so they can't silently drift apart the way
 * they previously did (the write path used to accept any string as a phone
 * number while the read path required Brazil phone format — meaning a
 * profile could be saved successfully and then fail to read back).
 */
export const StoredUserProfileSchema = z
  .object({
    commissionFee: z.coerce
      .number()
      .min(0, { message: "clients_validation_commission_range" })
      .max(100, { message: "clients_validation_commission_range" })
      .nullish(),
    validityPeriod: z.coerce
      .number()
      .int({ message: "clients_validation_validity_range" })
      .nonnegative({ message: "clients_validation_validity_range" })
      .nullish(),
    bankingInfo: BrazilBankingInfoSchema.nullish(),
    clientIds: z.array(z.uuid()).nullish(),
    branchIds: z.array(z.uuid()).nullish(),
    contactInfo: UserContactInfoSchema.nullish(),
  })
  .describe("Stored default profile for a user.");

export const UpsertUserProfileSchema = StoredUserProfileSchema.extend({
  name: z.string().max(100).optional(),
});
export type UpsertUserProfileInput = z.infer<typeof UpsertUserProfileSchema>;

export const UserProfileResponseSchema = StoredUserProfileSchema.extend({
  name: z.string().optional(),
});
export type UserProfileResponse = z.infer<typeof UserProfileResponseSchema>;

const ProfileFieldsSchema = z.object({
  name: z
    .string({ message: "profile_validation_name_required" })
    .trim()
    .min(1, { message: "profile_validation_name_required" })
    .optional(),
  picture: z.url({ message: "profile_validation_invalid_url" }).or(z.literal("")).optional(),
  photo: z
    .string()
    .max(2_796_204)
    .regex(/^[A-Za-z0-9+/]+={0,2}$/)
    .optional(),
});

export const UpdateProfileSchema = ProfileFieldsSchema.refine(
  (body) => body.name !== undefined || body.picture !== undefined || body.photo !== undefined,
  { message: "profile_validation_at_least_one_required" },
);

export type UpdateProfileInput = z.infer<typeof UpdateProfileSchema>;

export const UpdateProfileResponseSchema = z.object({
  name: z.string().nullish().meta({ visibleTo: "*" }),
  picture: z.string().nullish().meta({ visibleTo: "*" }),
});

/** The profile form always shows a name field, unlike the partial-update API. */
export const ProfileFormSchema = z.object({
  profileName: ProfileFieldsSchema.shape.name.unwrap(),
  pictureUrl: ProfileFieldsSchema.shape.picture.unwrap(),
  locale: z.enum(["en", "pt-BR"]),
});

export const LookupProfilesResponseSchema = z.record(
  z.string(),
  z.object({
    name: z.string().optional().meta({ visibleTo: COGNITO_INTERNAL_STAFF_GROUPS }),
    picture: z.string().optional().meta({ visibleTo: COGNITO_INTERNAL_STAFF_GROUPS }),
    email: z.string().optional().meta({ visibleTo: COGNITO_INTERNAL_STAFF_GROUPS }),
  }),
);

export const ThemePreference = z.enum(["light", "dark", "soft"]);
export type ThemePreference = z.infer<typeof ThemePreference>;

export const MyTasksViewPreference = z.enum(["table", "kanban"]);
export type MyTasksViewPreference = z.infer<typeof MyTasksViewPreference>;

const MyTasksGroupingPreference = z
  .enum(["none", "type"])
  .describe("Groups My Tasks by activity type (`type`) or lists them flat (`none`).");
export type MyTasksGroupingPreference = z.infer<typeof MyTasksGroupingPreference>;

/**
 * The caller's own UI preferences (`UsersTable`, `PREFERENCES` sort key).
 * Every key is optional and written independently: a PATCH sets only the
 * keys it carries, so each key must stay a self-contained top-level value.
 */
export const UserPreferencesSchema = z.object({
  theme: ThemePreference.optional().meta({ visibleTo: "*", description: "UI color theme." }),
  myTasksView: MyTasksViewPreference.optional().meta({
    visibleTo: "*",
    description: "Layout of the My Tasks page.",
  }),
  myTasksGrouping: MyTasksGroupingPreference.optional().meta({
    visibleTo: "*",
    description: "Grouping of the My Tasks table rows and kanban swimlanes.",
  }),
});
export type UserPreferences = z.infer<typeof UserPreferencesSchema>;

export const StoredUserPreferencesSchema = UserPreferencesSchema.extend({
  theme: UserPreferencesSchema.shape.theme.catch(undefined),
  myTasksView: UserPreferencesSchema.shape.myTasksView.catch(undefined),
  myTasksGrouping: UserPreferencesSchema.shape.myTasksGrouping.catch(undefined),
}).describe("Stored preferences; a value the current contract no longer accepts reads as unset.");
